import { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { Icon, Loader, Table } from "semantic-ui-react";
import { toast } from "../..";
import { ToggleToastLoading } from "../../actions/toastActions";
import SubtasksStatus from "../../components/SubtasksStatus/SubtasksStatus";
import { ITask } from "../../models/task";
import {
    downloadGromInputs,
    downloadGromOutputs,
    downloadInputs,
    downloadRaoResults,
    downloadReports,
} from "../../services/calculationsService";
import { AppState } from "../../store/configureStore";
import { RAOReportState } from "../../store/raoReportReducer";

type Props = {
    task: ITask;
};

export const TasksRow = ({ task }: Props) => {
    const dispatch = useDispatch();
    const user = useSelector((state: AppState) => state.user);
    const { loading } = useSelector((state: AppState) => state.toast);
    const formattedTaskName = useMemo(() => {
        if (task.name === "RAO") return "Security reserve estimation";
        else return task.name;
    }, [task.name]);
    const hasGromOptimizerData = useMemo(() => {
        if (task.subtasks.length === 0) return false;
        const message = JSON.parse(task.subtasks[0].message);
        const calcProps = message.CalcProps
            ? JSON.parse(message.CalcProps)
            : JSON.parse(message.calcProps);
        const jsonString = JSON.parse(calcProps.jsonString);
        const raoReport: RAOReportState = jsonString.raoReport;
        if (raoReport.gromOptimizerData) return true;
        else return false;
    }, [task.subtasks]);

    const handleDownloadRaoResults = async () => {
        dispatch(ToggleToastLoading(true));
        if (!(await downloadRaoResults(task.guid, task.id)))
            toast("Unable to download file, please try again", false);
        dispatch(ToggleToastLoading(false));
    };

    const handleDownloadInputs = async () => {
        dispatch(ToggleToastLoading(true));
        if (!(await downloadInputs(task.guid)))
            toast("Unable to download file, please try again", false);
        dispatch(ToggleToastLoading(false));
    };

    const handleDownloadReports = async () => {
        dispatch(ToggleToastLoading(true));
        if (!(await downloadReports(task.guid, task.id, task.calculationDate)))
            toast("Unable to download file, please try again", false);
        dispatch(ToggleToastLoading(false));
    };

    const handleDownloadGromInputs = async () => {
        dispatch(ToggleToastLoading(true));
        if (!(await downloadGromInputs(task.guid, task.id)))
            toast("Unable to download file, please try again", false);
        dispatch(ToggleToastLoading(false));
    };

    const handleDownloadGromOutputs = async () => {
        dispatch(ToggleToastLoading(true));
        if (!(await downloadGromOutputs(task.guid, task.id)))
            toast("Unable to download file, please try again", false);
        dispatch(ToggleToastLoading(false));
    };

    useEffect(() => {
        return () => {
            dispatch(ToggleToastLoading(false));
        };
    }, [dispatch]);

    return (
        <>
            <Table.Row id={`tableRow-tasksRow-${task.id}`}>
                <Table.Cell id={`tableCell-tasksRow-taskName-${task.id}`}>{formattedTaskName}</Table.Cell>
                <Table.Cell id={`tableCell-tasksRow-a-${task.id}`}>
                    {task.added.replace("T", " ").substring(0, task.added.indexOf("."))}
                </Table.Cell>
                <Table.Cell id={`tableCell-tasksRow-b-${task.id}`}>
                    <SubtasksStatus
                        failed={task.failedSubtasks}
                        success={task.successSubtasks}
                        running={task.runningSubtasks}
                        total={task.subtasks.length}
                    />
                </Table.Cell>
                <Table.Cell id={`tableCell-tasksRow-isCompleted-${task.id}`}>
                    {task.isComplete ? <Icon name="check" /> : <Icon name="close" />}
                </Table.Cell>
                <Table.Cell id={`tableCell-tasksRow-actions-${task.id}`} className="actions">
                    <Link
                        id={`link-tasksRow-a-${task.id}`}
                        className="tasks-action"
                        to={`/Calculations/${task.guid}/Tasks/${task.stage}/Subtasks`}
                    >
                        Details
                    </Link>
                    {task.name === "RAO" &&
                        (user.role === "Admin" || user.role === "SuperAdmin") &&
                        hasGromOptimizerData && (
                            <>
                                <span id={`span-tasksRow-a-${task.id}`}>|</span>
                                <span
                                    id={`span-tasksRow-b-${task.id}`}
                                    className="tasks-action"
                                    onClick={() => handleDownloadGromInputs()}
                                >
                                    Download GROM_OPT_INPUT
                                </span>
                                <span id={`span-tasksRow-c-${task.id}`}>|</span>
                                <span
                                    id={`span-tasksRow-d-${task.id}`}
                                    className="tasks-action"
                                    onClick={() => handleDownloadGromOutputs()}
                                >
                                    Download GROM_OPT_OUTPUT
                                </span>
                            </>
                        )}
                    {task.name === "RAO" && (
                        <>
                            <span id={`span-tasksRow-e-${task.id}`}>|</span>
                            <span id={`span-tasksRow-f-${task.id}`} className="tasks-action" onClick={() => handleDownloadInputs()}>
                                Download input files
                            </span>
                        </>
                    )}
                    {(task.isComplete || task.isFailed) && (
                        <>
                            <span id={`span-tasksRow-g-${task.id}`}>|</span>
                            <span
                                id={`span-tasksRow-h-${task.id}`}
                                className="tasks-action"
                                onClick={() =>
                                    task.name === "RAO"
                                        ? handleDownloadRaoResults()
                                        : handleDownloadReports()
                                }
                            >
                                Download&nbsp;
                                {task.successSubtasks != null && task.successSubtasks === task.subtasks.length ? "" : "partial "}
                                {task.name === "RAO" ? "output files" : "reports"}
                            </span>
                        </>
                    )}
                </Table.Cell>
            </Table.Row>
            {loading && (
                <Loader id={`loader-tasksRow-${task.id}`} className="download-loader" active inline size="large" indeterminate />
            )}
        </>
    );
};
